import React, { useEffect, useMemo, useRef, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Alert, Avatar, Box, Chip, Badge, Tooltip, CircularProgress, TextField, SpeedDial, SpeedDialAction, Fab, IconButton, InputAdornment, Paper, Snackbar, Typography, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemIcon, ListItemText, Button, Card, CardHeader, CardContent, CardActions, Divider, Link, LinearProgress, Stepper, Step, StepLabel, StepContent, Accordion, AccordionSummary, AccordionDetails, Slide, Fade,  } from '@mui/material';
import { alpha, useTheme, styled } from '@mui/material/styles';

import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MovieCreationIcon from "@mui/icons-material/MovieCreation";
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import SendIcon from '@mui/icons-material/Send';
import ScheduleIcon from '@mui/icons-material/Schedule';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LinkIcon from '@mui/icons-material/Link';
import CodeIcon from '@mui/icons-material/Code';
import WarningIcon from '@mui/icons-material/Warning';
import PaidIcon from '@mui/icons-material/Paid';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import EditIcon from '@mui/icons-material/Edit';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import PhoneIcon from '@mui/icons-material/Phone';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CampaignIcon from '@mui/icons-material/Campaign';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoIcon from '@mui/icons-material/Info';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LaunchIcon from '@mui/icons-material/Launch';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AssessmentIcon from '@mui/icons-material/Assessment';

// Enhanced Chat sub-components
import AlertDialog from '../AlertDialog';
import ChatBubble from './ChatBubble';
import ReadReceipt from './ReadReceipt';
import ReplyPreview from './ReplyPreview';
import ShippingAddressDialog from './ShippingAddressDialog';
import SelectAssetDialog from './../tools/selectfile';
import CreatorUploadFileDialog from '../../CreatorUser/CreatorPages/Dashboard/creatorUtils/uploadfile';
import PdfSigningDialog from './pdfsigning';
import CampaignVideoEditor from '../CampaignVideoEditor';
import EditVideoLinksDialog from './EditVideoLinksDialog';
import { Link as RouterLink } from 'react-router-dom';
import BaseBubble from './BaseBubble';

// Hooks & Utilities
import API from '../../API';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import useAuth from '../../Hooks/use-auth';
import { replaceConsoleError } from '../../Utils/toastNotifications';
import { useCreatorAuth } from '../../Hooks/creator-use-auth';
import useAlertDialog from './../useAlertDialog';
import useIsDesktop from '../../Hooks/useIsDesktop';
import { getFileType, isMessageMine, getTypingText } from '../../Utils/constants';
import { useSocket } from '../../Context/SocketContext';
import useMentions from './useMentions';
import MentionSuggestions from './MentionSuggestions';

/* ───────────────────────── Enhanced Progress Components ───────────────────────── */

const CompactProgressContainer = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.secondary.main, 0.08)})`,
  borderRadius: '16px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  overflow: 'hidden',
  boxShadow: theme.shadows[2],
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.secondary.main, 0.12)})`,
  }
}));

const ProgressStep = styled(Box)(({ theme, completed, active, position }) => {
  const size = 32;
  return {
    position: 'absolute',
    top: '50%',
    left: `${position}%`,
    transform: 'translate(-50%, -50%)',
    width: size,
    height: size,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: completed
      ? theme.palette.success.main
      : active
      ? theme.palette.primary.main
      : alpha(theme.palette.grey[400], 0.8),
    color: 'white',
    fontSize: '14px',
    fontWeight: 600,
    border: `2px solid ${theme.palette.background.paper}`,
    boxShadow: theme.shadows[2],
    zIndex: 10,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translate(-50%, -50%) scale(1.1)',
    }
  };
});

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 12,
  borderRadius: 0,
  backgroundColor: alpha(theme.palette.grey[300], 0.3),
  '& .MuiLinearProgress-bar': {
    borderRadius: 0,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  }
}));

const WelcomeDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '20px',
    maxWidth: '500px',
    background: theme.palette.background.paper, // Much simpler, just use paper background
    border: `1px solid ${theme.palette.divider}`, // Simple divider border
    boxShadow: theme.shadows[8], // Clean shadow instead of backdrop blur
  }
}));

const NextStepCard = styled(Card)(({ theme, priority }) => {
  const priorities = {
    high: {
      border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`, // Much lighter borders
      background: alpha(theme.palette.error.main, 0.02), // Very light background
    },
    medium: {
      border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
      background: alpha(theme.palette.warning.main, 0.02),
    },
    low: {
      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
      background: alpha(theme.palette.success.main, 0.02),
    },
  };

  return {
    ...priorities[priority] || priorities.low,
    borderRadius: '12px', // Slightly smaller radius
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-1px)', // Subtle hover effect
      boxShadow: theme.shadows[4],
    }
  };
});


/* ───────────────────────── Enhanced Attachment Components from Document 2 ───────────────────────── */

const ProgressCard = styled(Card)(({ theme, status }) => ({
  background: status === 'completed' 
    ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.main, 0.2)})`
    : status === 'in-progress'
    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.2)})`
    : `linear-gradient(135deg, ${alpha(theme.palette.grey[500], 0.05)}, ${alpha(theme.palette.grey[500], 0.1)})`,
  borderRadius: '16px',
  border: status === 'completed' 
    ? `1px solid ${alpha(theme.palette.success.main, 0.3)}`
    : status === 'in-progress'
    ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
    : `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

const AttachmentCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  textDecoration: 'none',
  display: 'block',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: theme.shadows[8],
    border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.secondary.main, 0.08)})`,
  },
  '&:active': {
    transform: 'translateY(-1px)',
  },
}));

const ProgressChip = styled(Chip)(({ theme, variant }) => {
  const variants = {
    success: {
      backgroundColor: alpha(theme.palette.success.main, 0.1),
      color: theme.palette.success.main,
      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
    },
    warning: {
      backgroundColor: alpha(theme.palette.warning.main, 0.1),
      color: theme.palette.warning.main,
      border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
    },
    error: {
      backgroundColor: alpha(theme.palette.error.main, 0.1),
      color: theme.palette.error.main,
      border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
    },
    info: {
      backgroundColor: alpha(theme.palette.info.main, 0.1),
      color: theme.palette.info.main,
      border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
    },
  };
  
  return {
    ...variants[variant] || variants.info,
    fontWeight: 600,
    fontSize: '0.75rem',
    height: 24,
  };
});

/* ───────────────────────── Progress Calculation ───────────────────────── */

const getStepIcon = (stepId) => {
  const iconProps = { sx: { fontSize: 16 } };
  switch (stepId) {
    case 'created': return <AssignmentIcon {...iconProps} />;
    case 'draft': return <CloudUploadIcon {...iconProps} />;
    case 'links': return <LinkIcon {...iconProps} />;
    case 'boost': return <CampaignIcon {...iconProps} />;
    case 'timeline': return <AccessTimeIcon {...iconProps} />;
    case 'payment': return <MonetizationOnIcon {...iconProps} />;
    default: return <RadioButtonUncheckedIcon {...iconProps} />;
  }
};

const calculateVideoProgress = (video, crossposts = []) => {
  const steps = [
    {
      id: 'created',
      label: 'Assignment',
      description: 'Video slot created',
      completed: true, // Always true if video exists
      required: true,
    },
    {
      id: 'draft',
      label: 'Content',
      description: 'Initial content uploaded',
      completed: !!(video.draftLink || video.submissionLink),
      required: true,
    },
    {
      id: 'links',
      label: 'Links',
      description: 'Live platform links added',
      completed: !!video.liveLink && crossposts.every(c => !!c.liveLink),
      required: true,
    },
    {
      id: 'boost',
      label: 'Boost',
      description: 'Promotional codes provided',
      completed: !!video.boostCode && crossposts.every(c => !!c.boostCode),
      required: true,
    },
    {
      id: 'timeline',
      label: 'Timeline',
      description: 'Delivery timeline met',
      completed: video.postDate ? new Date(video.postDate) <= new Date() : true,
      required: !!video.expectedDate,
    },
    {
      id: 'payment',
      label: 'Payment',
      description: 'Payment processed',
      completed: !!video.payoutComplete,
      required: true,
    },
  ];

  const requiredSteps = steps.filter(step => step.required);
  const completedSteps = requiredSteps.filter(step => step.completed);
  const currentStep = requiredSteps.findIndex(step => !step.completed);

  const progressPercentage = (completedSteps.length / requiredSteps.length) * 100;

  const status = progressPercentage === 100
    ? 'completed'
    : progressPercentage > 0
    ? 'in-progress'
    : 'not-started';

  return {
    steps: requiredSteps,
    completedCount: completedSteps.length,
    totalCount: requiredSteps.length,
    progressPercentage,
    currentStepIndex: currentStep === -1 ? requiredSteps.length - 1 : currentStep,
    status,
    nextAction: currentStep === -1 ? null : requiredSteps[currentStep].label
  };
};

/* ───────────────────────── Next Steps Guidance System ───────────────────────── */
const getNextStepsForUser = (isCreator, campaignStatus, videoProgresses, allMessages) => {
  const hasVideoContent = videoProgresses.some(vp => vp.completedCount >= 2); // Has draft content
  const hasLiveLinks = videoProgresses.some(vp => vp.steps.find(s => s.id === 'links')?.completed);
  const hasBoostCodes = videoProgresses.some(vp => vp.steps.find(s => s.id === 'boost')?.completed);
  const allCompleted = videoProgresses.every(vp => vp.status === 'completed');
  
  const recentMessages = allMessages.slice(-5);
  const hasRecentCreatorMessage = recentMessages.some(msg => !!msg.creator_id);
  const hasRecentBrandMessage = recentMessages.some(msg => !!msg.sender_id && !msg.creator_id);

  if (isCreator) {
    // Creator workflow - step by step pipeline
    if (!hasVideoContent) {
      return {
        title: "📝 Step 1: Create Your Content",
        subtitle: "Upload your video draft for brand review",
        priority: 'high',
        steps: [
          {
            title: "Review Campaign Requirements",
            description: "Check the campaign brief and brand guidelines shared in this chat",
            icon: <DescriptionOutlinedIcon />,
            completed: false
          },
          {
            title: "Create & Upload Video Draft",
            description: "Upload your video content using the attachment button below. This will be reviewed by the brand before going live.",
            icon: <MovieCreationIcon />,
            completed: false
          },
          {
            title: "Wait for Brand Approval",
            description: "The brand will review your content and provide feedback or approval",
            icon: <CheckCircleIcon />,
            completed: false
          }
        ]
      };
    }

    if (!hasLiveLinks) {
      return {
        title: "🚀 Step 2: Publish Your Content",
        subtitle: "Your content is approved - time to go live!",
        priority: 'medium',
        steps: [
          {
            title: "Publish on Required Platforms",
            description: "Post your approved video to the platforms specified in the campaign",
            icon: <PlayCircleOutlineIcon />,
            completed: false
          },
          {
            title: "Share Live Post URLs",
            description: "Add the links to your live posts so the brand can track performance and complete payment",
            icon: <LinkIcon />,
            completed: false
          },
          {
            title: "Add Any Promotional Codes",
            description: "Include any discount codes or affiliate links provided by the brand",
            icon: <CodeIcon />,
            completed: hasBoostCodes
          }
        ]
      };
    }

    if (!hasBoostCodes && hasLiveLinks) {
      return {
        title: "📈 Step 3: Add Promotional Codes",
        subtitle: "Complete your campaign setup",
        priority: 'medium',
        steps: [
          {
            title: "Add Boost/Promo Codes",
            description: "Include any promotional codes, discount links, or affiliate tracking provided by the brand",
            icon: <CodeIcon />,
            completed: false
          },
          {
            title: "Monitor Performance",
            description: "Keep an eye on your post engagement and respond to comments",
            icon: <TrendingUpIcon />,
            completed: false
          }
        ]
      };
    }

    if (allCompleted) {
      return {
        title: "✅ Campaign Complete!",
        subtitle: "All requirements met - payment processing",
        priority: 'low',
        steps: [
          {
            title: "All Deliverables Complete",
            description: "You've successfully completed all campaign requirements. The brand will process payment shortly.",
            icon: <TaskAltIcon />,
            completed: true
          },
          {
            title: "Payment Processing",
            description: "Your payment is being processed and will be completed soon",
            icon: <PaidIcon />,
            completed: false
          }
        ]
      };
    }

    return {
      title: "⏰ Complete Remaining Steps",
      subtitle: "Check the progress bar for pending items",
      priority: 'medium',
      steps: [
        {
          title: "Review Campaign Progress",
          description: "Use the progress bar above to see which deliverables still need completion",
          icon: <AssessmentIcon />,
          completed: false
        }
      ]
    };

  } else {
    // Brand workflow - managing creator pipeline
    if (!hasVideoContent) {
      return {
        title: "📋 Step 1: Creator Content Creation",
        subtitle: "Waiting for creator to submit draft content",
        priority: 'low',
        steps: [
          {
            title: "Share Additional Guidelines",
            description: "Upload any additional brand assets, style guides, or requirements if needed",
            icon: <CloudUploadIcon />,
            completed: false
          },
          {
            title: "Monitor Creator Progress",
            description: "The creator is working on content. Be available to answer questions in this chat.",
            icon: <ChatBubbleOutlineIcon />,
            completed: hasRecentBrandMessage
          },
          {
            title: "Prepare for Review",
            description: "Creator will upload draft content for your approval soon",
            icon: <VideoLibraryIcon />,
            completed: false
          }
        ]
      };
    }

    if (hasVideoContent && !hasLiveLinks) {
      return {
        title: "👀 Step 2: Review & Approve Content",
        subtitle: "Creator has submitted content for review",
        priority: 'high',
        steps: [
          {
            title: "Review Submitted Content",
            description: "Check the video content uploaded by the creator against campaign requirements",
            icon: <VideoLibraryIcon />,
            completed: false
          },
          {
            title: "Provide Feedback or Approval",
            description: "Comment in this chat with any feedback, or approve the content for publication",
            icon: <CheckCircleIcon />,
            completed: false
          },
          {
            title: "Track Publication",
            description: "Once approved, creator will publish and share live links",
            icon: <LaunchIcon />,
            completed: false
          }
        ]
      };
    }

    if (hasLiveLinks && !allCompleted) {
      return {
        title: "📊 Step 3: Track & Complete Campaign",
        subtitle: "Content is live - monitor and finalize",
        priority: 'medium',
        steps: [
          {
            title: "Monitor Campaign Performance",
            description: "Track engagement, views, and campaign metrics from the live posts",
            icon: <TrendingUpIcon />,
            completed: false
          },
          {
            title: "Verify All Requirements Met",
            description: "Ensure all deliverables are complete including promotional codes",
            icon: <CheckCircleIcon />,
            completed: hasBoostCodes
          },
          {
            title: "Process Payment",
            description: "Complete payment once all campaign requirements are verified",
            icon: <PaidIcon />,
            completed: false
          }
        ]
      };
    }

    if (allCompleted) {
      return {
        title: "🎉 Campaign Successfully Completed!",
        subtitle: "All deliverables complete",
        priority: 'low',
        steps: [
          {
            title: "Campaign Completed",
            description: "All creator deliverables have been completed successfully",
            icon: <TaskAltIcon />,
            completed: true
          },
          {
            title: "Payment Processed",
            description: "Creator payment has been completed",
            icon: <PaidIcon />,
            completed: true
          },
          {
            title: "Review Campaign Results",
            description: "Analyze final performance metrics and campaign ROI",
            icon: <AssessmentIcon />,
            completed: false
          }
        ]
      };
    }

    return {
      title: "🔄 Manage Campaign Progress",
      subtitle: "Continue overseeing campaign completion",
      priority: 'medium',
      steps: [
        {
          title: "Monitor Pipeline",
          description: "Use the progress bar above to track remaining deliverables and support your creator",
          icon: <AutoFixHighIcon />,
          completed: false
        }
      ]
    };
  }
};

/* ───────────────────────── Updated Welcome Dialog Component - Lighter Styling ───────────────────────── */

const WelcomeNextStepsDialog = ({ open, onClose, isCreator, campaignStatus, videoProgresses, allMessages, onVideoDetailsOpen }) => {
  const theme = useTheme();
  const nextSteps = getNextStepsForUser(isCreator, campaignStatus, videoProgresses, allMessages);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return theme.palette.error.main;
      case 'medium': return theme.palette.warning.main;
      case 'low': return theme.palette.success.main;
      default: return theme.palette.primary.main;
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return <NotificationsActiveIcon sx={{ color: theme.palette.error.main }} />;
      case 'medium': return <InfoIcon sx={{ color: theme.palette.warning.main }} />;
      case 'low': return <LightbulbIcon sx={{ color: theme.palette.success.main }} />;
      default: return <InfoIcon sx={{ color: theme.palette.primary.main }} />;
    }
  };

  return (
    <WelcomeDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 400 }}
    >
      <DialogTitle sx={{ 
        background: alpha(getPriorityColor(nextSteps.priority), 0.03), // Much lighter background
        border: `1px solid ${alpha(getPriorityColor(nextSteps.priority), 0.1)}`, // Lighter border
        borderRadius: '12px 12px 0 0',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        pb: 2
      }}>
        {getPriorityIcon(nextSteps.priority)}
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}> {/* Use text.primary instead of priority color */}
            Campaign Workflow Guide
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, color: getPriorityColor(nextSteps.priority) }}>
            {nextSteps.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {nextSteps.subtitle}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="body1" sx={{ mb: 3, fontWeight: 500, color: 'text.primary' }}>
          Follow these steps to complete your campaign:
        </Typography>

        <List>
          {nextSteps.steps.map((step, index) => (
            <ListItem key={index} sx={{ px: 0, py: 1.5 }}>
              <ListItemIcon>
                <Avatar 
                  sx={{ 
                    bgcolor: step.completed ? theme.palette.success.main : alpha(getPriorityColor(nextSteps.priority), 0.1),
                    color: step.completed ? 'white' : getPriorityColor(nextSteps.priority),
                    width: 48,
                    height: 48
                  }}
                >
                  {step.completed ? <CheckCircleIcon /> : step.icon}
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600,
                      textDecoration: step.completed ? 'line-through' : 'none',
                      opacity: step.completed ? 0.7 : 1,
                      fontSize: '1.1rem',
                      color: 'text.primary' // Ensure good contrast
                    }}
                  >
                    {step.title}
                  </Typography>
                }
                secondary={
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      opacity: step.completed ? 0.7 : 1,
                      mt: 0.5,
                      fontSize: '0.95rem'
                    }}
                  >
                    {step.description}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>

        {videoProgresses.length > 0 && (
          <NextStepCard priority={nextSteps.priority} sx={{ mt: 3 }}>
            <CardHeader
              avatar={<VideoLibraryIcon color="primary" />}
              title={
                <Typography variant="h6" sx={{ color: 'text.primary' }}>
                  Campaign Progress Overview
                </Typography>
              }
              subheader="Click to view detailed video progress and manage deliverables"
              action={
                <IconButton onClick={onVideoDetailsOpen}>
                  <PlayCircleOutlineIcon />
                </IconButton>
              }
            />
            <CardContent sx={{ pt: 0 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {videoProgresses.map((progress, index) => (
                  <Chip
                    key={index}
                    size="small"
                    label={`Video ${index + 1}: ${Math.round(progress.progressPercentage)}%`}
                    color={progress.status === 'completed' ? 'success' : progress.status === 'in-progress' ? 'primary' : 'default'}
                    variant={progress.status === 'not-started' ? 'outlined' : 'filled'}
                  />
                ))}
              </Box>
            </CardContent>
          </NextStepCard>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button onClick={onClose} variant="outlined" size="large">
          Got it!
        </Button>
        {videoProgresses.length > 0 && (
          <Button onClick={onVideoDetailsOpen} variant="contained" startIcon={<VideoLibraryIcon />} size="large">
            View Progress Details
          </Button>
        )}
      </DialogActions>
    </WelcomeDialog>
  );
};
/* ───────────────────────── Compact Campaign Progress Bar ───────────────────────── */

const CompactCampaignProgressBar = ({ 
  videos = [], 
  creatorName, 
  campaignName, 
  allMessages, 
  metadata, 
  onManualRefresh, 
  isRefreshing, 
  lastUpdateTime, 
  isPolling,
  onProgressClick,
  onGuidanceOpen,
  onlineUsersCount = 0,
  socketConnected = false
}) => {
  const theme = useTheme();

  const campaignVideos = useMemo(() => {
    const videoMessages = allMessages.filter(msg =>
      msg.attachments?.attachment_type === 'campaign_video'
    );

    const videos = [];
    videoMessages.forEach(msg => {
      const videoData = msg.attachments?.data?.video;
      const crossposts = msg.attachments?.data?.crossposts || [];
      if (videoData) {
        videos.push({
          ...videoData,
          crossposts
        });
      }
    });

    return videos;
  }, [allMessages]);

  const calculatedCreatorName = useMemo(() => {
    return metadata?.creator_name || metadata?.participants?.find(p => p.is_creator)?.name || 'Creator';
  }, [metadata]);

  const calculatedCampaignName = useMemo(() => {
    return metadata?.campaign_name || metadata?.campaign?.name || 'Campaign';
  }, [metadata]);

  const overallProgress = useMemo(() => {
    if (!videos.length && !campaignVideos.length) {
      return { 
        progressPercentage: 0, 
        status: 'not-started',
        completedSteps: 0,
        totalSteps: 0,
        videoProgresses: [],
        steps: [
          { id: 'created', label: 'Assignment', completed: false, required: true },
          { id: 'draft', label: 'Content', completed: false, required: true },
          { id: 'links', label: 'Links', completed: false, required: true },
          { id: 'boost', label: 'Boost', completed: false, required: true },
          { id: 'timeline', label: 'Timeline', completed: false, required: true },
          { id: 'payment', label: 'Payment', completed: false, required: true },
        ]
      };
    }

    const videoProgresses = (videos.length ? videos : campaignVideos)
      .filter(video => !video.parentId)
      .map(video => {
        const crossposts = (videos.length ? videos : campaignVideos).filter(v => v.parentId === video.id);
        return calculateVideoProgress(video, crossposts);
      });

    const totalSteps = videoProgresses.reduce((sum, vp) => sum + vp.totalCount, 0);
    const completedSteps = videoProgresses.reduce((sum, vp) => sum + vp.completedCount, 0);

    const progressPercentage = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

    const status = progressPercentage === 100
      ? 'completed'
      : progressPercentage > 0
      ? 'in-progress'
      : 'not-started';

    const representativeSteps = videoProgresses.length > 0 
      ? videoProgresses[0].steps 
      : [
          { id: 'created', label: 'Assignment', completed: false, required: true },
          { id: 'draft', label: 'Content', completed: false, required: true },
          { id: 'links', label: 'Links', completed: false, required: true },
          { id: 'boost', label: 'Boost', completed: false, required: true },
          { id: 'timeline', label: 'Timeline', completed: false, required: true },
          { id: 'payment', label: 'Payment', completed: false, required: true },
        ];

    return {
      progressPercentage,
      status,
      completedSteps,
      totalSteps,
      videoProgresses,
      steps: representativeSteps
    };
  }, [videos, campaignVideos]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return theme.palette.success.main;
      case 'in-progress': return theme.palette.primary.main;
      default: return theme.palette.grey[400];
    }
  };

  return (
    <CompactProgressContainer elevation={2} sx={{ mb: 1 }} onClick={onProgressClick}>
      {/* Header Row */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        p: 2, 
        pb: 1,
        minHeight: 48
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
            <MovieCreationIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ 
              fontWeight: 700, 
              color: theme.palette.primary.main,
              fontSize: '1rem',
              lineHeight: 1.2
            }}>
              Campaign Chat
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {creatorName || calculatedCreatorName} • {campaignName || calculatedCampaignName}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Tooltip title="Click for next steps guidance">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onGuidanceOpen();
              }}
              sx={{ 
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
              }}
            >
              <LightbulbIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            </IconButton>
          </Tooltip>

          <Chip
            size="small"
            label={`${Math.round(overallProgress.progressPercentage)}%`}
            color={overallProgress.status === 'completed' ? 'success' : overallProgress.status === 'in-progress' ? 'primary' : 'default'}
            sx={{ fontWeight: 600, fontSize: '0.75rem', minWidth: 50 }}
          />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={onlineUsersCount > 0 ? `${onlineUsersCount} user${onlineUsersCount > 1 ? 's' : ''} online` : (socketConnected ? 'No one online' : 'Disconnected')}>
              <FiberManualRecordIcon
                sx={{
                  fontSize: 8,
                  color: onlineUsersCount > 0 ? 'success.main' : 'warning.main',
                }}
              />
            </Tooltip>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', minWidth: 30 }}>
              {onlineUsersCount > 0 
                ? `${onlineUsersCount} online` 
                : (socketConnected ? 'Offline' : 'Disconnected')}
            </Typography>
          </Box>

          <Tooltip title="Refresh messages">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onManualRefresh();
              }}
              disabled={isRefreshing}
              sx={{ p: 0.5 }}
            >
              <RefreshIcon
                sx={{
                  fontSize: 16,
                  animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  }
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Progress Bar with Embedded Steps */}
      <Box sx={{ position: 'relative', px: 2, pb: 2 }}>
        <StyledLinearProgress
          variant="determinate"
          value={overallProgress.progressPercentage}
          sx={{
            '& .MuiLinearProgress-bar': {
              background: `linear-gradient(90deg, ${getStatusColor(overallProgress.status)}, ${alpha(getStatusColor(overallProgress.status), 0.8)})`,
            }
          }}
        />
        
        {/* Progress Steps Embedded in Bar */}
        {overallProgress.steps.map((step, index) => {
          const position = ((index + 0.5) / overallProgress.steps.length) * 100;
          const isActive = index === overallProgress.currentStepIndex;
          
          return (
            <Tooltip key={step.id} title={`${step.label}: ${step.description}`} placement="top">
              <ProgressStep
                completed={step.completed}
                active={isActive}
                position={position}
              >
                {getStepIcon(step.id)}
              </ProgressStep>
            </Tooltip>
          );
        })}
      </Box>

      {/* Quick Stats Row */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        px: 2,
        pb: 1,
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
          {overallProgress.completedSteps}/{overallProgress.totalSteps} steps • {overallProgress.videoProgresses.length} video{overallProgress.videoProgresses.length !== 1 ? 's' : ''}
        </Typography>
        
        {overallProgress.nextAction && (
          <Chip
            size="small"
            label={`Next: ${overallProgress.nextAction}`}
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        )}
      </Box>
    
    </CompactProgressContainer>
  );
};

/* ───────────────────────── Enhanced Attachment Bubble from Document 2 ───────────────────────── */

const AddressLine = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(0.25),
  color: theme.palette.text.secondary,
  fontSize: "0.9rem",
}));

const GetIcon = (attachmentType, fileData) => {
  switch (attachmentType) {
    case "invoice":
      return <ReceiptLongIcon fontSize="small" />;
    case "file":
      return fileData?.content_type?.startsWith("image/")
        ? <ImageOutlinedIcon fontSize="small" />
        : <InsertDriveFileOutlinedIcon fontSize="small" />;
    case "campaign":
      return <CampaignOutlinedIcon fontSize="small" />;
    case "roster":
      return <GroupOutlinedIcon fontSize="small" />;
    case "mediakit":
      return <DescriptionOutlinedIcon fontSize="small" />;
    case "creator":
      return <PersonOutlinedIcon fontSize="small" />;
    case "address":
      return <LocalShippingIcon fontSize="small" />;
    case "campaign_video":
      return <MovieCreationIcon fontSize="small" />;
    default:
      return <InsertDriveFileOutlinedIcon fontSize="small" />;
  }
};

const isImageFile = (fileData) =>
  !!fileData && getFileType(fileData.file_url) === "image";

const paymentReady = (videoWithCross) =>
  !!videoWithCross.liveLink &&
  !!videoWithCross.boostCode &&
  videoWithCross.crossposts.every(
    (c) => !!c.liveLink && !!c.boostCode
  ) &&
  !videoWithCross.payoutComplete;

function ImageModal({ open, onClose, imageUrl, filename }) {
  const [isZoomed, setIsZoomed] = useState(false);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = filename || 'image';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.9)',
          backgroundImage: 'none',
        }
      }}
    >
      <DialogActions sx={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}>
        <Tooltip title="Download">
          <IconButton onClick={handleDownload} sx={{ color: 'white' }}>
            <DownloadIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={isZoomed ? "Zoom Out" : "Zoom In"}>
          <IconButton onClick={() => setIsZoomed(!isZoomed)} sx={{ color: 'white' }}>
            <ZoomInIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogActions>
      
      <DialogContent 
        sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '70vh',
          overflow: isZoomed ? 'auto' : 'hidden'
        }}
      >
        <Box
          component="img"
          src={imageUrl}
          alt={filename || "Attachment"}
          sx={{
            maxWidth: isZoomed ? 'none' : '100%',
            maxHeight: isZoomed ? 'none' : '100%',
            width: isZoomed ? 'auto' : 'auto',
            height: isZoomed ? 'auto' : 'auto',
            objectFit: 'contain',
            borderRadius: 1,
            cursor: isZoomed ? 'grab' : 'pointer',
            transition: 'transform 0.3s ease',
            transform: isZoomed ? 'scale(1.5)' : 'scale(1)',
            '&:active': {
              cursor: isZoomed ? 'grabbing' : 'pointer',
            }
          }}
          onClick={() => setIsZoomed(!isZoomed)}
        />
      </DialogContent>
      
      {filename && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'white', opacity: 0.8 }}>
            {filename}
          </Typography>
        </Box>
      )}
    </Dialog>
  );
}

function RenderAttachment({
  att,
  convMeta,
  onEditFile,
  onVideoLink,
  isUser,
  onCompletePayment,
  onRequestPayment,
  completePaymentLoading,
  requestPaymentLoading,
}) {
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const theme = useTheme();

  const attachmentType = att.attachment_type;
  const fileData = att.file;

  if (
    attachmentType !== "invoice" &&
    !att.file &&
    !att.creator &&
    !att.roster &&
    !att.campaign &&
    !att.data
  ) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading attachment...</Typography>
      </Box>
    );
  }

  const handleImageClick = (imageUrl, filename) => {
    setSelectedImage({ url: imageUrl, filename });
    setImageModalOpen(true);
  };

  /* 1) Invoice - Enhanced card sizing */
  if (attachmentType === "invoice") {
    const invoice = att.data?.invoice_data;
    if (!invoice)
      return <Typography color="error">Invalid invoice data.</Typography>;

    return (
      <ProgressCard variant="outlined" sx={{ minWidth: 280, maxWidth: 320 }}>
        <CardHeader
          avatar={<ReceiptLongIcon color="primary" />}
          title={
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Invoice #{invoice.id ?? "----"}
            </Typography>
          }
          subheader={`PO ${invoice.po_number ?? "N/A"}`}
          sx={{ pb: 1 }}
        />
        <Divider />
        <CardContent sx={{ py: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, fontSize: '0.9rem' }}>
            Amount: <strong>${invoice.amount}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, fontSize: '0.9rem' }}>
            Client: <strong>{invoice.client_name}</strong>
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: "0.85rem", opacity: 0.8, mt: 1 }}
          >
            Status: {invoice.invoice_status ?? "pending"}
          </Typography>
        </CardContent>
        <CardActions sx={{ justifyContent: "space-between", px: 2, pt: 0 }}>
          <Button
            size="small"
            component={RouterLink}
            to={`/creatorinvoice/${invoice.id}`}
            variant="outlined"
          >
            View Details
          </Button>
          <Tooltip title="Share invoice">
            <IconButton
              component={RouterLink}
              to={`/creatorinvoice/${invoice.id}`}
              color="primary"
            >
              <ShareOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </CardActions>
      </ProgressCard>
    );
  }

  /* 2) File */
  if (attachmentType === "file") {
    if (!fileData)
      return <Typography variant="body2">No file data found.</Typography>;

    if (isImageFile(fileData)) {
      return (
        <>
          <Box sx={{ position: "relative", display: "inline-block" }}>
            <Box
              component="img"
              src={fileData.file_url}
              alt="Attachment"
              onClick={() => handleImageClick(fileData.file_url, fileData.filename)}
              sx={{
                maxWidth: 200,
                maxHeight: 200,
                borderRadius: 2,
                cursor: "pointer",
                transition: ".3s",
                "&:hover": { boxShadow: 3, transform: "scale(1.02)" },
              }}
            />

            {!!fileData.id && (
              <Tooltip title="Edit file">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditFile?.(fileData);
                  }}
                  sx={{ position: "absolute", top: 4, right: 4, bgcolor: "rgba(255,255,255,0.9)" }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <ImageModal
            open={imageModalOpen}
            onClose={() => setImageModalOpen(false)}
            imageUrl={selectedImage?.url}
            filename={selectedImage?.filename}
          />
        </>
      );
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton component={RouterLink} to={`/files/${fileData.id}`} color="primary">
          <ShareOutlinedIcon />
        </IconButton>
        <Typography variant="body2">{fileData.filename}</Typography>
        <Chip
          label={(fileData.content_type?.split("/")[1] ?? "file").toUpperCase()}
          size="small"
          sx={{ ml: 1 }}
          color="secondary"
        />
        {!!fileData.id && (
          <Tooltip title="Edit file">
            <IconButton onClick={() => onEditFile?.(fileData)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }

  /* 3) Campaign - Enhanced card design */
  if (attachmentType === "campaign") {
    const campaign = att.campaign;
    const campaignName = campaign?.name ?? "Unnamed Campaign";
    const campaignId = campaign?.id ?? "";
    const campaignDescription = campaign?.description || campaign?.brief;
    const campaignBudget = campaign?.budget;
    const campaignDeadline = campaign?.deadline || campaign?.end_date;

    return (
      <AttachmentCard
        component={RouterLink}
        to={`/campaigns/${campaignId}`}
        sx={{ minWidth: 280, maxWidth: 380, textDecoration: 'none' }}
      >
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 40, height: 40 }}>
              <CampaignOutlinedIcon />
            </Avatar>
          }
          title={
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {campaignName}
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Campaign Project
            </Typography>
          }
          action={
            <Tooltip title="View Campaign">
              <IconButton size="small" sx={{ color: 'primary.main' }}>
                <LaunchIcon />
              </IconButton>
            </Tooltip>
          }
          sx={{ pb: 1 }}
        />
        
        {(campaignDescription || campaignBudget || campaignDeadline) && (
          <>
            <Divider />
            <CardContent sx={{ py: 2 }}>
              {campaignDescription && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.4
                  }}
                >
                  {campaignDescription}
                </Typography>
              )}
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {campaignBudget && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PaidIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    <Typography variant="caption" color="text.secondary">
                      ${campaignBudget}
                    </Typography>
                  </Box>
                )}
                
                {campaignDeadline && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarTodayIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(campaignDeadline).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </>
        )}
        
        <Divider />
        <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Chip 
            label="Campaign" 
            size="small" 
            color="primary" 
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
          <Typography variant="caption" color="primary" sx={{ fontWeight: 500 }}>
            View Details →
          </Typography>
        </CardActions>
      </AttachmentCard>
    );
  }

  /* 4) Creator - Enhanced card design */
  if (attachmentType === "creator") {
    const creator = att.creator;
    const creatorName = creator?.creator ?? creator?.name ?? "Unnamed Creator";
    const creatorId = creator?.creator ?? creator?.id ?? "";
    const creatorAvatar = creator?.pfphref || creator?.avatar_url;
    const creatorPlatform = creator?.platform || creator?.primary_platform;
    const creatorFollowers = creator?.followers || creator?.follower_count;
    const creatorBio = creator?.bio || creator?.description;
    const creatorLocation = creator?.location;

    return (
      <AttachmentCard
        component={RouterLink}
        to={`/creators/${creatorId}`}
        sx={{ minWidth: 280, maxWidth: 380, textDecoration: 'none' }}
      >
        <CardHeader
          avatar={
            <Avatar 
              src={creatorAvatar} 
              sx={{ width: 48, height: 48, border: `2px solid ${theme.palette.primary.main}` }}
            >
              <PersonOutlinedIcon />
            </Avatar>
          }
          title={
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {creatorName}
            </Typography>
          }
          subheader={
            <Box sx={{ mt: 0.5 }}>
              {creatorPlatform && (
                <Typography variant="body2" color="text.secondary">
                  {creatorPlatform} Creator
                </Typography>
              )}
              {creatorFollowers && (
                <Typography variant="caption" color="text.secondary">
                  {creatorFollowers.toLocaleString()} followers
                </Typography>
              )}
            </Box>
          }
          action={
            <Tooltip title="View Profile">
              <IconButton size="small" sx={{ color: 'primary.main' }}>
                <LaunchIcon />
              </IconButton>
            </Tooltip>
          }
          sx={{ pb: 1 }}
        />
        
        {(creatorBio || creatorLocation) && (
          <>
            <Divider />
            <CardContent sx={{ py: 2 }}>
              {creatorBio && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.4
                  }}
                >
                  {creatorBio}
                </Typography>
              )}
              
              {creatorLocation && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <BusinessIcon sx={{ fontSize: 16, color: 'info.main' }} />
                  <Typography variant="caption" color="text.secondary">
                    {creatorLocation}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </>
        )}
        
        <Divider />
        <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Chip 
            label="Creator" 
            size="small" 
            color="secondary" 
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
          <Typography variant="caption" color="primary" sx={{ fontWeight: 500 }}>
            View Profile →
          </Typography>
        </CardActions>
      </AttachmentCard>
    );
  }

  /* 5) Address */
  if (attachmentType === "address") {
    const addressData = att.data;
    if (!addressData)
      return <Typography color="error">Invalid address data.</Typography>;

    return (
      <ProgressCard variant="outlined" sx={{ maxWidth: 350 }}>
        <CardHeader avatar={<LocalShippingIcon color="primary" />} title="Delivery Address" />
        <Divider />
        <CardContent>
          <Typography variant="body1" fontWeight={600}>
            {addressData.recipient}
          </Typography>
          <AddressLine variant="body2">{addressData.address}</AddressLine>
          <AddressLine variant="body2">
            {addressData.city}, {addressData.state} {addressData.zip}
          </AddressLine>
          <AddressLine variant="body2">{addressData.country}</AddressLine>
          {addressData.instructions && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {addressData.instructions}
            </Alert>
          )}
        </CardContent>
        <Divider />
        <CardActions sx={{ justifyContent: "space-between", px: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <PhoneIcon color="primary" sx={{ fontSize: "0.9rem", mr: 0.5 }} />
            <Typography variant="body2">{addressData.phone}</Typography>
          </Box>
        </CardActions>
      </ProgressCard>
    );
  }

  /* 6) Enhanced Campaign video with progress tracking */
  if (attachmentType === "campaign_video") {
    const videoData = att.data;
    if (!videoData?.video)
      return (<Box p={2}><Typography color="error">Video data not found.</Typography></Box>);

    const video = {
      ...videoData.video,
      crossposts: videoData.crossposts ?? [],
    };
    
    const progress = calculateVideoProgress(video, video.crossposts);
    const eligible = paymentReady(video);

    const getStatusColor = () => {
      switch (progress.status) {
        case 'completed': return theme.palette.success.main;
        case 'in-progress': return theme.palette.primary.main;
        default: return theme.palette.grey[400];
      }
    };

    return (
      <ProgressCard variant="outlined" status={progress.status} sx={{ maxWidth: 420, minWidth: 350 }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: getStatusColor() }}>
              <MovieCreationIcon />
            </Avatar>
          }
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">Campaign Video</Typography>
              <ProgressChip 
                size="small"
                label={`${Math.round(progress.progressPercentage)}%`}
                variant={progress.status === 'completed' ? 'success' : progress.status === 'in-progress' ? 'info' : 'warning'}
              />
            </Box>
          }
          subheader={`#${String(video.id).padStart(3, "0")} • ${video.promotionPlatform} ${video.promotionType}`}
        />
        
        {/* Progress Bar */}
        <Box sx={{ px: 2, mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={progress.progressPercentage}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: alpha(theme.palette.grey[300], 0.3),
              '& .MuiLinearProgress-bar': {
                background: `linear-gradient(90deg, ${getStatusColor()}, ${alpha(getStatusColor(), 0.7)})`,
                borderRadius: 4,
              }
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {progress.completedCount}/{progress.totalCount} steps complete
            </Typography>
            {progress.nextAction && (
              <Typography variant="caption" color="primary">
                Next: {progress.nextAction}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider />

        {/* Quick status indicators */}
        <CardContent sx={{ pt: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {progress.steps.map((step) => {
              const Icon = step.completed ? CheckCircleIcon : 
                          step.id === progress.steps[progress.currentStepIndex]?.id ? ScheduleIcon : 
                          RadioButtonUncheckedIcon;
              
              return (
                <Tooltip key={step.id} title={step.description}>
                  <Chip
                    size="small"
                    icon={<Icon sx={{ fontSize: 16 }} />}
                    label={step.label}
                    variant={step.completed ? "filled" : "outlined"}
                    color={step.completed ? "success" : step.id === progress.steps[progress.currentStepIndex]?.id ? "primary" : "default"}
                    sx={{ fontSize: '0.7rem', height: 24 }}
                  />
                </Tooltip>
              );
            })}
          </Box>

          {/* Main video details */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Platform Link</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {video.liveLink ? (
                  <Link href={video.liveLink} target="_blank" sx={{ fontSize: '0.875rem' }}>
                    <LinkIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    Live
                  </Link>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <WarningIcon sx={{ fontSize: 16, mr: 0.5, color: 'warning.main' }} />
                    Pending
                  </Typography>
                )}
              </Box>
            </Box>
            
            <Box>
              <Typography variant="caption" color="text.secondary">Boost Code</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {video.boostCode ? (
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                    <CodeIcon sx={{ fontSize: 16, mr: 0.5, color: 'success.main' }} />
                    Ready
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <WarningIcon sx={{ fontSize: 16, mr: 0.5, color: 'warning.main' }} />
                    Pending
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          {/* Cross-posts summary */}
          {video.crossposts.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Cross-posts ({video.crossposts.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {video.crossposts.map((crosspost, index) => (
                  <Chip
                    key={crosspost.id}
                    size="small"
                    label={crosspost.promotionPlatform}
                    color={crosspost.liveLink && crosspost.boostCode ? "success" : "warning"}
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </CardContent>

        <Divider />
        <CardActions sx={{ justifyContent: "space-between", px: 2 }}>
          <Button 
            onClick={() => onVideoLink(video.id)}
            startIcon={<VideoLibraryIcon />}
            size="small"
          >
            Details
          </Button>

          {isUser ? (
            <Button
              variant="contained"
              color={progress.status === 'completed' ? "success" : "primary"}
              size="small"
              startIcon={<PaidIcon />}
              disabled={!eligible || completePaymentLoading}
              onClick={() => onCompletePayment?.(video.id)}
            >
              {completePaymentLoading ? "Processing..." : eligible ? "Complete Payment" : "Not Ready"}
            </Button>
          ) : (
            <Button
              variant="contained"
              color={progress.status === 'completed' ? "success" : "primary"}
              size="small"
              startIcon={<RequestQuoteIcon />}
              disabled={!eligible || requestPaymentLoading}
              onClick={() => onRequestPayment?.(video.id)}
            >
              {requestPaymentLoading ? "Requesting..." : eligible ? "Request Payment" : "Not Ready"}
            </Button>
          )}
        </CardActions>
      </ProgressCard>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary">
      Unknown attachment type: {attachmentType}
    </Typography>
  );
}

function EnhancedAttachmentBubble({
  msg,
  userId,
  isTcc,
  isUser,
  metadata,
  handleDeleteMessage,
  deleteMessageLoading,
  onEditFile,
  handleVideoLink,
  onCompletePayment,
  onRequestPayment,
  completePaymentLoading,
  requestPaymentLoading,
}) {
  const isUserMessage = isMessageMine(userId, msg);
  const att = msg.attachments;
  if (!att) return null;

  const attachmentType = att.attachment_type ?? "unknown";

  return (
    <Tooltip
      title={`${attachmentType.charAt(0).toUpperCase() + attachmentType.slice(1)} attachment`}
    >
      <BaseBubble
        isUserMessage={isUserMessage}
        isTcc={isTcc}
        handleDeleteMessage={() => handleDeleteMessage(msg.id)}
        deleteMessageLoading={deleteMessageLoading}
        customColors
        padding={["address", "campaign_video", "campaign", "creator"].includes(attachmentType) ? 0 : undefined}
      >
        <RenderAttachment
          att={att}
          convMeta={metadata}
          onEditFile={onEditFile}
          onVideoLink={handleVideoLink}
          isUser={isUser}
          onCompletePayment={onCompletePayment}
          onRequestPayment={onRequestPayment}
          completePaymentLoading={completePaymentLoading}
          requestPaymentLoading={requestPaymentLoading}
        />
      </BaseBubble>
    </Tooltip>
  );
}

/* ───────────────────────── Constants & Helpers ───────────────────────── */
const MAX_MESSAGES_WITHOUT_RESPONSE = 10;
const SCROLL_TIMEOUT = 100;
const SNACKBAR_DURATION = 6000;
const MAX_MESSAGE_ROWS = 3;
const POLLING_INTERVAL = 10000; // Fallback polling when socket disconnected
const NAV_BAR_HEIGHT = 86;
const SHOW_POLLING_INDICATOR_EVERY = 6;

const generateTempId = () => `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const getIcon = (attachmentType) => {
  switch (attachmentType) {
    case 'video':
      return <VideoLibraryIcon color='primary' />;
    case 'address':
      return <LocalShippingIcon color='primary' />;
    default:
      return undefined;
  }
};

/* ───────────────────────── Enhanced Campaign Chat Screen ───────────────────────── */
const EnhancedCampaignChatScreen = forwardRef(function EnhancedCampaignChatScreen(
  {
    children,
    conversationId,
    campaignId,
    creatorId,
    showWarning = true,
    specificReaderId = null,
    chatSpacing = 1,
    margin = 2,
    sx = {},
    onDeliverablesUpdate,
  },
  ref
) {
  const theme = useTheme();
  const { getCurrrentUser } = useAuth();
  const userInfo = getCurrrentUser();
  const { creatorToken } = useCreatorAuth();
  const queryClient = useQueryClient();
  const isDesktop = useIsDesktop();
  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  const listRef = useRef(null);
  const lastMessageId = useRef(null);
  const pollingTimeoutRef = useRef(null);
  const isPollingRef = useRef(false);
  const pollCountRef = useRef(0);

  // WebSocket integration
  const {
    isConnected: socketConnected,
    joinConversation,
    leaveConversation,
    sendMessage: socketSendMessage,
    sendTypingStatus,
    markAsRead: socketMarkAsRead,
    addReaction: socketAddReaction,
    removeReaction: socketRemoveReaction,
    typingUsers,
    getTypingUsers,
    onlineUsers,
    getOnlineUsers,
    isUserOnline,
    roomPresence,
    getRoomOnlineUsers
  } = useSocket();

  /* ------------------------------------------------------------------ */
  /*  State Management                                                  */
  /* ------------------------------------------------------------------ */
  const [message, setMessage] = useState('');
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [chatError, setChatError] = useState(null);
  const [currentMessage, setCurrentMessage] = useState(null);
  const [showMessageCapAlert, setShowMessageCapAlert] = useState(false);
  const [isManualRefresh, setIsManualRefresh] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [showPollingIndicator, setShowPollingIndicator] = useState(false);

  // Campaign-specific dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [creatorUploadDialogOpen, setCreatorUploadDialogOpen] = useState(false);
  const [creatorShippingAddress, setCreatorShippingAddress] = useState(false);
  const [openVideo, setOpenVideo] = useState(false);
  const [openVideoLink, setOpenVideoLink] = useState(false);
  const [videoLinkId, setVideoLinkId] = useState(null);
  const [pdfSigningDialogOpen, setPdfSigningDialogOpen] = useState(false);
  const [fileForSigning, setFileForSigning] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);

  // NEW: Reply and Reaction States
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [isQuoting, setIsQuoting] = useState(false);
  const [reactionLoading, setReactionLoading] = useState(new Set());
  const [snack, setSnack] = useState({ msg: '', sev: 'info' });
  
  // Optimistic messages for REST fallback
  const [optimisticMessages, setOptimisticMessages] = useState([]);

  // NEW: Welcome Dialog States
  const [welcomeDialogOpen, setWelcomeDialogOpen] = useState(false);
  const [nextStepsDialogOpen, setNextStepsDialogOpen] = useState(false);
  const [hasShownWelcome, setHasShownWelcome] = useState(false);

  // NEW: Request payment loading state
  const [requestPaymentLoading, setRequestPaymentLoading] = useState(false);

  /* ------------------------------------------------------------------ */
  /*  Derived Variables                                                 */
  /* ------------------------------------------------------------------ */
  const userId = specificReaderId || userInfo?.id || creatorToken?.creator_user?.username || null;
  const userCompany = userInfo?.company_name;
  const isTcc = userCompany === "TCC";
  const isUser = !specificReaderId && !!userInfo && !creatorToken;
  const isCreator = !!creatorToken && !isUser;

  /* ------------------------------------------------------------------ */
  /*  Data Fetching                                                     */
  /* ------------------------------------------------------------------ */

  // Metadata query
  const {
    data: metadata,
    isLoading: metadataLoading,
    refetch: metadataRefetch,
    error: metadataError
  } = useQuery(
    ["campaign_messages", "metadata", conversationId],
    () => {
      return API.conversations.getMetadata(
        conversationId,
        specificReaderId !== null ? { reader_id: specificReaderId } : undefined
      );
    },
    {
      enabled: !!conversationId,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 30,
      onError: (err) => {
        replaceConsoleError('Campaign metadata fetch error', err);
      },
    }
  );

  // Messages query with optimized polling
  const {
    data: conversation,
    isLoading: messagesLoading,
    isFetching: messagesRefetching,
    refetch: messagesRefetch,
    error: messagesError,
    dataUpdatedAt: lastFetchTime
  } = useQuery(
    ["campaign_messages", conversationId],
    () => {
      return API.conversations.getMessages(
        conversationId,
        specificReaderId !== null ? { reader_id: specificReaderId } : undefined
      );
    },
    {
      enabled: !!conversationId,
      refetchOnWindowFocus: false, // Never refetch on window focus
      refetchOnMount: false,       // Never refetch on remount (strict mode)
      refetchOnReconnect: false,   // Never refetch on network reconnect
      staleTime: 1000 * 30,
      cacheTime: 1000 * 60 * 10,
      onError: (err) => {
        replaceConsoleError('Campaign messages fetch error', err);
        setChatError('Failed to load messages');
      },
      onSuccess: (data) => {
        setChatError(null);
        setLastUpdateTime(new Date());
      }
    }
  );

  // @mention support
  const mentionParticipants = useMemo(
    () => metadata?.participants || conversation?.participants || [],
    [metadata?.participants, conversation?.participants]
  );
  const mentions = useMentions(mentionParticipants, userId);

  /* ------------------------------------------------------------------ */
  /*  WebSocket Room Management                                         */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!conversationId) return;

    if (socketConnected) {
      joinConversation(conversationId);
      socketMarkAsRead(conversationId);
    }

    return () => {
      if (socketConnected) {
        leaveConversation(conversationId);
      }
    };
  }, [conversationId, socketConnected, joinConversation, leaveConversation, socketMarkAsRead]);

  /* ------------------------------------------------------------------ */
  /*  Fallback Polling System (only when socket disconnected)           */
  /* ------------------------------------------------------------------ */
  const startPolling = useCallback(() => {
    // Don't poll if socket is connected
    if (isPollingRef.current || !conversationId || socketConnected) return;

    isPollingRef.current = true;
    pollCountRef.current = 0;

    const poll = async () => {
      // Stop polling if socket became connected
      if (socketConnected) {
        stopPolling();
        return;
      }

      try {
        if (document.hidden || !conversationId) {
          pollingTimeoutRef.current = setTimeout(poll, POLLING_INTERVAL * 2);
          return;
        }

        pollCountRef.current++;

        const shouldShowIndicator = pollCountRef.current % SHOW_POLLING_INDICATOR_EVERY === 0;
        if (shouldShowIndicator) {
          setShowPollingIndicator(true);
        }

        await messagesRefetch();

        if (shouldShowIndicator) {
          setTimeout(() => setShowPollingIndicator(false), 1000);
        }

        pollingTimeoutRef.current = setTimeout(poll, POLLING_INTERVAL);
      } catch (error) {
        console.error('Polling error:', error);
        setShowPollingIndicator(false);
        pollingTimeoutRef.current = setTimeout(poll, POLLING_INTERVAL * 2);
      }
    };

    pollingTimeoutRef.current = setTimeout(poll, POLLING_INTERVAL);
  }, [conversationId, messagesRefetch, socketConnected]);

  const stopPolling = useCallback(() => {
    isPollingRef.current = false;
    setShowPollingIndicator(false);
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  }, []);

  // Start/stop polling based on socket connection status
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else if (conversationId && !socketConnected) {
        startPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Only start polling if socket is NOT connected
    if (conversationId && !document.hidden && !socketConnected) {
      startPolling();
    } else if (socketConnected) {
      stopPolling();
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopPolling();
    };
  }, [conversationId, startPolling, stopPolling, socketConnected]);

  /* ------------------------------------------------------------------ */
  /*  Message Processing                                                */
  /* ------------------------------------------------------------------ */
  const allMessages = useMemo(() => {
    const serverMessages = conversation?.messages || [];
    // Combine server messages with optimistic messages, filtering out any optimistic
    // messages that have been confirmed (matching by client_temp_id)
    const serverTempIds = new Set(
      serverMessages
        .filter(m => m.client_temp_id)
        .map(m => m.client_temp_id)
    );
    const pendingOptimistic = optimisticMessages.filter(
      m => !serverTempIds.has(m.client_temp_id)
    );
    return [...serverMessages, ...pendingOptimistic];
  }, [conversation?.messages, optimisticMessages]);

  /* ------------------------------------------------------------------ */
  /*  Online Status                                                    */
  /* ------------------------------------------------------------------ */
  // Per-room presence
  const roomOnline = useMemo(() => getRoomOnlineUsers(conversationId), [getRoomOnlineUsers, conversationId, roomPresence]);
  const onlineUsersCount = roomOnline.online_count || 0;
  const onlineUsersList = roomOnline.online_users || [];

  // Typing indicator
  const currentTypingUsers = useMemo(() => {
    const users = getTypingUsers(conversationId) || [];
    return users.filter(u => String(u.user_id) !== String(userId));
  }, [getTypingUsers, conversationId, typingUsers, userId]);

  /* ------------------------------------------------------------------ */
  /*  Video Progress Calculation and Deliverables Update               */
  /* ------------------------------------------------------------------ */
  const videoProgresses = useMemo(() => {
    const videoMessages = allMessages.filter(msg =>
      msg.attachments?.attachment_type === 'campaign_video'
    );

    const progresses = [];
    videoMessages.forEach(msg => {
      const videoData = msg.attachments?.data?.video;
      const crossposts = msg.attachments?.data?.crossposts || [];
      if (videoData) {
        const progress = calculateVideoProgress({
          ...videoData,
          crossposts
        }, crossposts);
        progresses.push(progress);
      }
    });

    return progresses;
  }, [allMessages]);

  // Update deliverables count
  useEffect(() => {
    if (onDeliverablesUpdate) {
      const pendingDeliverables = videoProgresses.filter(vp => 
        vp.status !== 'completed' && vp.nextAction
      );
      onDeliverablesUpdate(pendingDeliverables);
    }
  }, [videoProgresses, onDeliverablesUpdate]);

  // NEW: Show welcome dialog for new conversations automatically
  useEffect(() => {
    if (allMessages.length <= 3 && metadata && !hasShownWelcome && !messagesLoading && !metadataLoading) {
      const timer = setTimeout(() => {
        setWelcomeDialogOpen(true);
        setHasShownWelcome(true);
      }, 1500); // Show after 1.5 seconds
      return () => clearTimeout(timer);
    }
  }, [allMessages.length, metadata, hasShownWelcome, messagesLoading, metadataLoading]);

  /* ------------------------------------------------------------------ */
  /*  Mutations                                                         */
  /* ------------------------------------------------------------------ */
  const { mutate: deleteMessage, isLoading: deleteMessageLoading } = useMutation(
    (msgId) => API.conversations.deleteMessage(msgId),
    {
      onSuccess: () => {
        messagesRefetch();
      },
      onError: (error) => {
        console.error('Delete message error:', error);
        openDialog(
          "Error",
          `Error while deleting message: ${error?.response?.data?.error || error.message}`
        );
      }
    }
  );

  // Payment mutations
  const { mutate: completePayment, isLoading: completePaymentLoading } = useMutation(
    (videoId) => API.campaigns.completeVideo(campaignId, { creator: creatorId, video: videoId }),
    {
      onSuccess: () => {
        messagesRefetch();
        openDialog(
          "Success",
          "Payment completed successfully.",
          closeDialog,
          closeDialog,
          "Ok",
          null
        );
      },
      onError: (error) => {
        console.error('Complete payment error:', error);
        openDialog(
          "Error",
          `Error completing payment: ${error?.response?.data?.error || error.message}`
        );
      }
    }
  );

  // NEW: Modified request payment handler that sends a message instead of calling API
 
  // Enhanced send message with optimistic updates and reply support
  const { mutate: sendMessage, isLoading: sendMessageLoading } = useMutation(
    ({ id, input }) => API.conversations.sendMessage(id, input),
    {
      onMutate: async ({ input }) => {
        const tempId = generateTempId();
        const optimisticMessage = {
          id: tempId,
          client_temp_id: tempId,
          message: input.message || 'Attachment',
          sender_id: isUser ? userId : null,
          creator_id: !isUser ? userId : null,
          created_at: new Date().toISOString(),
          isOptimistic: true,
          is_pending: true,
          attachments: input.attachment ? {
            attachment_type: input.attachment.type,
            data: input.attachment.data,
            label: input.attachment.label
          } : null,
          read_receipts: {},
          reply_to_message_id: input.reply_to_message_id || null,
          is_quote: input.is_quote || false,
          reply_to: replyToMessage ? {
            id: replyToMessage.id,
            message: replyToMessage.message,
            sender_name: replyToMessage.sender?.first_name || replyToMessage.creator_id || 'User',
            created_at: replyToMessage.created_at
          } : null
        };

        setOptimisticMessages(prev => [...prev, optimisticMessage]);
        
        setMessage('');
        setSelectedAttachment(null);
        setReplyToMessage(null);
        setIsQuoting(false);

        setTimeout(() => {
          if (listRef.current) {
            listRef.current.scrollTo({
              top: listRef.current.scrollHeight,
              behavior: "smooth"
            });
          }
        }, 50);

        return { tempId };
      },
      onSuccess: (data, variables, context) => {
        if (context?.tempId) {
          setOptimisticMessages(prev =>
            prev.filter(msg => msg.client_temp_id !== context.tempId)
          );
        }
        messagesRefetch();
      },
      onError: (error, variables, context) => {
        console.error("Error sending message:", error);

        if (context?.tempId) {
          // Mark as failed instead of removing
          setOptimisticMessages(prev =>
            prev.map(msg => 
              msg.client_temp_id === context.tempId 
                ? { ...msg, is_pending: false, is_failed: true }
                : msg
            )
          );
        }

        setMessage(variables.input.message || '');
        if (variables.input.attachment) {
          setSelectedAttachment(variables.input.attachment);
        }
        if (variables.input.reply_to_message_id) {
          setReplyToMessage(replyToMessage);
          setIsQuoting(variables.input.is_quote || false);
        }

        handleSendMessageError(error);
      }
    }
  );
 const handleRequestPayment = useCallback(async (videoId) => {
    setRequestPaymentLoading(true);
    
    try {
      const inputData = {
        message: "Deliverables are complete, please complete payment from your side",
      };

      sendMessage({ id: conversationId, input: inputData });
      
      setSnack({ 
        msg: "Payment request message sent successfully!", 
        sev: "success" 
      });
    } catch (error) {
      console.error('Error sending payment request message:', error);
      openDialog(
        "Error",
        `Error sending message: ${error?.response?.data?.error || error.message}`
      );
    } finally {
      setRequestPaymentLoading(false);
    }
  }, [conversationId, sendMessage, openDialog, setSnack]);

  /* ------------------------------------------------------------------ */
  /*  Event Handlers                                                    */
  /* ------------------------------------------------------------------ */
  const userMessagesCount = useMemo(() => {
    if (!allMessages || allMessages.length === 0) return 0;
    
    const userMessages = allMessages.filter((m) => isMessageMine(userId, m));
    const otherMessages = allMessages.filter((m) => !isMessageMine(userId, m));
    
    if (otherMessages.length > 0) {
      // Sort by time to ensure we get the actual last message from other party
      const sortedOtherMessages = [...otherMessages].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      const lastOtherMsgTime = new Date(sortedOtherMessages[0].created_at).getTime();
      
      // Count only user messages sent AFTER the last response from other party
      return userMessages.filter(
        (m) => new Date(m.created_at).getTime() > lastOtherMsgTime
      ).length;
    }
    
    return userMessages.length;
  }, [allMessages, userId]);

  // Auto-scroll
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (listRef.current && allMessages.length > 0) {
        const latestMessage = allMessages[allMessages.length - 1];
        if (latestMessage.id !== lastMessageId.current) {
          listRef.current.scrollTo({
            top: listRef.current.scrollHeight,
            behavior: "smooth"
          });
          lastMessageId.current = latestMessage.id;
        }
      }
    }, SCROLL_TIMEOUT);
    return () => clearTimeout(timeoutId);
  }, [allMessages]);

  const handleManualRefresh = useCallback(async () => {
    setIsManualRefresh(true);
    try {
      await Promise.all([metadataRefetch(), messagesRefetch()]);
    } finally {
      setIsManualRefresh(false);
    }
  }, [metadataRefetch, messagesRefetch]);

  const isMessageAllowed = useCallback((text) => {
    const prohibitedKeywords = [
      "text me", "contact me", "contact", "@gmail.com",
      "whatsapp", "convenient contact"
    ];
    const phoneRegex = /(?:\d[\s-]?){10,}/;
    const urlRegex = /https?:\/\/[^\s]+/g;
    const textNoUrls = text.replace(urlRegex, '');
    const lowerText = textNoUrls.toLowerCase();

    if (prohibitedKeywords.some(k => lowerText.includes(k))) {
      return false;
    }
    return !phoneRegex.test(textNoUrls);
  }, []);

  const handleSendMessage = useCallback(async () => {
    if (userMessagesCount >= MAX_MESSAGES_WITHOUT_RESPONSE && !isTcc) {
      setShowMessageCapAlert(true);
      return;
    }

    if (!isMessageAllowed(message)) {
      openDialog(
        "Error",
        "Your message contains prohibited content. Please refrain from sharing personal contact information."
      );
      return;
    }

    if ((!message || message.trim() === '') && !selectedAttachment) {
      openDialog("Error", "Can't send an empty message");
      return;
    }

    if (!conversationId) {
      openDialog("Error", "No valid conversation. Please contact support if this persists.");
      return;
    }

    const inputData = {
      message,
      ...(specificReaderId && { reader_id: specificReaderId }),
      ...(selectedAttachment && { attachment: selectedAttachment }),
      ...(replyToMessage && { 
        reply_to_message_id: replyToMessage.id,
        is_quote: isQuoting 
      })
    };

    // Use socket when connected, REST API as fallback
    if (socketConnected) {
      const tempId = generateTempId();
      const optimisticMessage = {
        id: tempId, // Temporary ID, will be replaced by server
        client_temp_id: tempId,
        message: message || 'Attachment',
        sender_id: isCreator ? null : userId,
        creator_id: isCreator ? userId : null,
        created_at: new Date().toISOString(),
        isOptimistic: true,
        is_pending: true, // Show single tick (pending state)
        attachments: selectedAttachment ? {
          attachment_type: selectedAttachment.type,
          data: selectedAttachment.data,
          label: selectedAttachment.label
        } : null,
        read_receipts: {},
        reply_to_message_id: replyToMessage?.id || null,
        is_quote: isQuoting,
        reply_to: replyToMessage ? {
          id: replyToMessage.id,
          message: replyToMessage.message,
          sender_name: replyToMessage.sender?.first_name || replyToMessage.creator_id || 'User',
          created_at: replyToMessage.created_at
        } : null
      };

      // ✅ Unified state: update query cache directly
      queryClient.setQueryData(["campaign_messages", conversationId], (old) => ({
        ...old,
        messages: [...(old?.messages || []), optimisticMessage]
      }));

      setMessage('');
      setSelectedAttachment(null);
      setReplyToMessage(null);
      setIsQuoting(false);

      setTimeout(() => {
        if (listRef.current) {
          listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: "auto" });
        }
      }, 0);

      // Send via socket with correct payload structure
      socketSendMessage({
        conversation_id: conversationId,
        message: message,
        attachment_id: selectedAttachment?.data?.id || null,
        reply_to_message_id: replyToMessage?.id || null,
        is_quote: isQuoting,
        client_temp_id: tempId
      });
    } else {
      sendMessage({ id: conversationId, input: inputData });
    }
  }, [
    message,
    selectedAttachment,
    conversationId,
    specificReaderId,
    userMessagesCount,
    isTcc,
    isMessageAllowed,
    openDialog,
    sendMessage,
    replyToMessage,
    isQuoting,
    socketConnected,
    socketSendMessage,
    isCreator,
    userId,
    queryClient
  ]);

  const handleSendMessageError = useCallback((error) => {
    if (error?.response?.data?.error === "forbidden") {
      openDialog(
        "Error",
        "This message was flagged as containing prohibited content. Please refrain from sharing personal contact information.",
        closeDialog,
        closeDialog,
        "Ok",
        null
      );
      return;
    }
    openDialog(
      "Error",
      `Error while sending message: ${error?.response?.data?.error || error.message}`
    );
  }, [openDialog, closeDialog]);

  const { mutate: addReaction } = useMutation(
      ({ messageId, reactionType }) => API.conversations.addReaction(messageId, reactionType),
      {
        onSuccess: () => {
          setSnack({ msg: 'Reaction added!', sev: 'success' });
          messagesRefetch();
        },
        onError: (error) => {
          replaceConsoleError('Error adding reaction', error);
          setSnack({ msg: 'Failed to add reaction', sev: 'error' });
        }
      }
    );
  
    const { mutate: removeReaction } = useMutation(
      ({ messageId, reactionType }) => API.conversations.removeReaction(messageId, reactionType),
      {
        onSuccess: () => {
          setSnack({ msg: 'Reaction removed!', sev: 'success' });
          messagesRefetch();
        },
        onError: (error) => {
          replaceConsoleError('Error removing reaction', error);
          setSnack({ msg: 'Failed to remove reaction', sev: 'error' });
        }
      }
    );

  const handleReactionAdd = useCallback(async (messageId, reactionType) => {
      if (reactionLoading.has(messageId)) return;
      
      setReactionLoading(prev => new Set(prev.add(messageId)));
      
      try {
        if (socketConnected && conversationId) {
          socketAddReaction(conversationId, messageId, reactionType);
          setSnack({ msg: 'Reaction added!', sev: 'success' });
        } else {
          addReaction({ messageId, reactionType });
        }
      } finally {
        setReactionLoading(prev => {
          const newSet = new Set(prev);
          newSet.delete(messageId);
          return newSet;
        });
      }
    }, [addReaction, reactionLoading, socketConnected, conversationId, socketAddReaction]);
  
    const handleReactionRemove = useCallback(async (messageId, reactionType) => {
      if (reactionLoading.has(messageId)) return;
      
      setReactionLoading(prev => new Set(prev.add(messageId)));
      
      try {
        if (socketConnected && conversationId) {
          socketRemoveReaction(conversationId, messageId, reactionType);
          setSnack({ msg: 'Reaction removed!', sev: 'success' });
        } else {
          removeReaction({ messageId, reactionType });
        }
      } finally {
        setReactionLoading(prev => {
          const newSet = new Set(prev);
          newSet.delete(messageId);
          return newSet;
        });
      }
    }, [removeReaction, reactionLoading, socketConnected, conversationId, socketRemoveReaction]);

  /* ------------------------------------------------------------------ */
  /*  Campaign-Specific Attachment Handlers                            */
  /* ------------------------------------------------------------------ */
  const handleVideoLink = (videoId) => {
    setVideoLinkId(videoId);
    setOpenVideoLink(true);
  };

  const handleFileSelect = useCallback((file) => {
    const isImage = getFileType(file.filename) === 'image';
    setSelectedAttachment({
      type: 'file',
      label: file.filename,
      data: file,
      preview: isImage ? file.file_url : undefined
    });
    setUploadDialogOpen(false);
    setMessage('');
  }, []);

  const handleShippingSelect = useCallback((form) => {
    setCreatorShippingAddress(false);
    if (!form) return;
    setSelectedAttachment({
      type: 'address',
      label: form.address + '...',
      data: { data: form }
    });
    setMessage('');
  }, []);

  const handleCreatorFile = useCallback((file) => {
    const baseInput = specificReaderId ? { reader_id: specificReaderId } : {};
    if (file.status === 'uploaded_to_firebase') {
      sendMessage({
        id: conversationId,
        input: {
          ...baseInput,
          message: '',
          attachment: { type: 'file', data: { id: file.file_id } }
        }
      });
    } else {
      sendMessage({
        id: conversationId,
        input: {
          ...baseInput,
          message: `Large attachments folder: ${file.redirect_url}`
        }
      });
      openDialog(
        'Alert',
        "A Google Drive folder was created for this upload. Opening in a new window...",
        () => window.open(file.redirect_url, '_blank'),
        () => window.open(file.redirect_url, '_blank')
      );
    }
    setCreatorUploadDialogOpen(false);
    setMessage('');
  }, [conversationId, specificReaderId, openDialog, sendMessage]);

  const handleCreatorUploadError = useCallback((err) => {
    console.error('Creator upload error:', err);
    setCreatorUploadDialogOpen(false);
    openDialog(
      "Error",
      `Error while sending message: ${err?.response?.data?.error || err.message}`
    );
  }, [openDialog]);

  const handleDeleteMessage = useCallback((messageId) => {
    if (isTcc) {
      openDialog(
        "Warning",
        "Are you sure you want to delete this message?",
        () => {
          closeDialog();
          deleteMessage(messageId);
        },
        closeDialog,
        "Yes",
        "No"
      );
    }
  }, [isTcc, openDialog, closeDialog, deleteMessage]);

  const handleEditFile = (file) => {
    setFileForSigning(file);
    setPdfSigningDialogOpen(true);
  };

  // Progress click handler to open video details
  const handleProgressClick = useCallback(() => {
    const firstVideoMessage = allMessages.find(msg =>
      msg.attachments?.attachment_type === 'campaign_video'
    );
    
    if (firstVideoMessage) {
      const videoId = firstVideoMessage.attachments?.data?.video?.id;
      if (videoId) {
        handleVideoLink(videoId);
      }
    } else {
      setOpenVideo(true);
    }
  }, [allMessages]);

  // Welcome and guidance dialog handlers
  const handleWelcomeClose = useCallback(() => {
    setWelcomeDialogOpen(false);
  }, []);

  const handleGuidanceOpen = useCallback(() => {
    setNextStepsDialogOpen(true);
  }, []);

  const handleGuidanceClose = useCallback(() => {
    setNextStepsDialogOpen(false);
  }, []);

  const handleVideoDetailsFromWelcome = useCallback(() => {
    setWelcomeDialogOpen(false);
    const firstVideoMessage = allMessages.find(msg =>
      msg.attachments?.attachment_type === 'campaign_video'
    );
    
    if (firstVideoMessage) {
      const videoId = firstVideoMessage.attachments?.data?.video?.id;
      if (videoId) {
        handleVideoLink(videoId);
      }
    } else {
      setOpenVideo(true);
    }
  }, [allMessages]);

  const handleVideoDetailsFromGuidance = useCallback(() => {
    setNextStepsDialogOpen(false);
    const firstVideoMessage = allMessages.find(msg =>
      msg.attachments?.attachment_type === 'campaign_video'
    );
    
    if (firstVideoMessage) {
      const videoId = firstVideoMessage.attachments?.data?.video?.id;
      if (videoId) {
        handleVideoLink(videoId);
      }
    } else {
      setOpenVideo(true);
    }
  }, [allMessages]);

  // Reply and reaction handlers
  const handleReply = useCallback((message) => {
    setReplyToMessage(message);
    setIsQuoting(false);
    setTimeout(() => {
      const input = document.querySelector('textarea[name="campaign-chat-message"]');
      if (input) input.focus();
    }, 100);
  }, []);

  const handleQuote = useCallback((message) => {
    setReplyToMessage(message);
    setIsQuoting(true);
    setTimeout(() => {
      const input = document.querySelector('textarea[name="campaign-chat-message"]');
      if (input) input.focus();
    }, 100);
  }, []);

  const handleCancelReply = useCallback(() => {
    setReplyToMessage(null);
    setIsQuoting(false);
  }, []);

  const scrollToMessage = useCallback((messageId) => {
    if (!messageId) return;
    
    const messageElement = document.getElementById(`msg-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center'
      });
      
      messageElement.style.transition = 'background-color 0.3s ease';
      messageElement.style.backgroundColor = alpha(theme.palette.primary.main, 0.1);
      
      setTimeout(() => {
        messageElement.style.backgroundColor = 'transparent';
      }, 2000);
    } else {
      if (listRef.current) {
        listRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => {
          const retryElement = document.getElementById(`msg-${messageId}`);
          if (retryElement) {
            retryElement.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
          }
        }, 500);
      }
    }
  }, [theme.palette.primary.main]);

  const handleReactionChange = useCallback((data) => {
    setSnack({ 
      msg: `${data.reaction ? 'Reaction added' : 'Reaction removed'}!`, 
      sev: "success" 
    });
  }, []);

  /* ------------------------------------------------------------------ */
  /*  Imperative Handle                                                 */
  /* ------------------------------------------------------------------ */
  useImperativeHandle(ref, () => ({
    scrollToTop() {
      listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    },
    refreshMessages() {
      messagesRefetch();
    },
    scrollToMessage,
    scrollToFirstDeliverable() {
      const firstVideoMessage = allMessages.find(msg =>
        msg.attachments?.attachment_type === 'campaign_video'
      );
      if (firstVideoMessage) {
        scrollToMessage(firstVideoMessage.id);
      }
    }
  }));

  /* ------------------------------------------------------------------ */
  /*  Render                                                            */
  /* ------------------------------------------------------------------ */
  return (
    <>
      <Box
        id='enhanced-campaign-chat-holder'
        sx={{
          ...sx,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: '100%',
          pb: (theme) =>
            window.location.pathname.includes("/con") ? 0 : `${NAV_BAR_HEIGHT}px`,
        }}
      >
        {/* Compact Campaign Progress Bar with Integrated Controls */}
        <CompactCampaignProgressBar
          videos={[]}
          creatorName={metadata?.creator_name}
          campaignName={metadata?.campaign_name}
          allMessages={allMessages}
          metadata={metadata}
          onManualRefresh={handleManualRefresh}
          isRefreshing={isManualRefresh}
          lastUpdateTime={lastUpdateTime}
          isPolling={isPollingRef.current}
          onProgressClick={handleProgressClick}
          onGuidanceOpen={handleGuidanceOpen}
          onlineUsersCount={onlineUsersCount}
          socketConnected={socketConnected}
        />

        {/* Messages Container */}
        <Box
          display="flex"
          flexDirection="column"
          sx={{
            minHeight: 0,
            paddingInline: margin,
            paddingBlockStart: 1,
            minHeight: "300px",
            minWidth: "0px",
            width: "100%",
            overflowY: "auto",
            overflowX: "clip",
            flex: 1,
            gap: chatSpacing,
            '&::-webkit-scrollbar': {
              width: 6,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.text.primary, 0.2),
              borderRadius: 3,
            },
          }}
          ref={listRef}
        >
          {/* Loading States */}
          {messagesLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {metadataError && (
            <Paper
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                color: 'warning.main',
                textAlign: 'center'
              }}
            >
              <Typography>
                {metadataError?.response?.status === 403
                  ? 'Access denied: you are not a participant in this conversation.'
                  : metadataError?.response?.status === 404
                  ? 'Conversation metadata not found. It may be private or removed.'
                  : `Error loading conversation metadata: ${metadataError?.response?.data?.error || metadataError.message}`}
              </Typography>
              <Button
                onClick={handleManualRefresh}
                sx={{ mt: 1 }}
                variant="outlined"
                size="small"
              >
                Retry
              </Button>
            </Paper>
          )}

          {messagesError && (
            <Paper
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.error.main, 0.1),
                color: 'error.main',
                textAlign: 'center'
              }}
            >
              <Typography>
                Error loading messages: {messagesError?.response?.data?.error || messagesError.message}
              </Typography>
              <Button
                onClick={handleManualRefresh}
                sx={{ mt: 1 }}
                variant="outlined"
                size="small"
              >
                Retry
              </Button>
            </Paper>
          )}

          {chatError && (
            <Typography color="error" sx={{ textAlign: 'center', p: 2 }}>
              {chatError}
            </Typography>
          )}

          {allMessages.length === 0 && !messagesError && !messagesLoading && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              color: "text.secondary",
              textAlign: "center"
            }}>
              <VideoLibraryIcon sx={{ fontSize: 48, mb: 2, color: 'primary.main' }} />
              <Typography variant="h6" gutterBottom>
                Campaign Chat Ready
              </Typography>
              <Typography variant="body2">
                Share video updates, assets, and coordinate your campaign here!
              </Typography>
              <Button 
                variant="outlined" 
                startIcon={<LightbulbIcon />}
                onClick={handleGuidanceOpen}
                sx={{ mt: 2 }}
              >
                Show Next Steps
              </Button>
            </Box>
          )}

          {/* Enhanced Message Rendering with Reply/Reaction Support */}
          {allMessages.map((msg) => {
            const isUserMessage = isMessageMine(userId, msg);
            const justify = isUserMessage ? "flex-end" : "flex-start";
            const isSimpleMsg = !msg.attachments;

            return (
              <Box
                key={msg.id}
                id={`msg-${msg.id}`}
                sx={{
                  display: "flex",
                  justifyContent: justify,
                  maxWidth: "100%",
                  transition: "all 0.2s",
                  position: 'relative'
                }}
              >

                {msg.attachments && (
                  <EnhancedAttachmentBubble
                    msg={msg}
                    userId={userId}
                    isTcc={isTcc}
                    isUser={isUser}
                    metadata={metadata}
                    handleDeleteMessage={handleDeleteMessage}
                    deleteMessageLoading={deleteMessageLoading}
                    onEditFile={handleEditFile}
                    handleVideoLink={handleVideoLink}
                    onCompletePayment={completePayment}
                    onRequestPayment={handleRequestPayment}
                    completePaymentLoading={completePaymentLoading}
                    requestPaymentLoading={requestPaymentLoading}
                  />
                )}

                {/* Enhanced ChatBubble with reactions and reply click */}
                {isSimpleMsg && (
                  <ChatBubble
                    msg={msg}
                    userId={userId}
                    isTcc={isTcc}
                    handleDeleteMessage={handleDeleteMessage}
                    deleteMessageLoading={deleteMessageLoading}
                    handleReadReceipts={(selectedMsg) => setCurrentMessage(selectedMsg)}
                    participants={conversation?.participants}
                    conversationId={conversationId}
                    onReactionChange={handleReactionChange}
                    onReactionAdd={handleReactionAdd}           // ✅ ADDED
                    onReactionRemove={handleReactionRemove}     // ✅ ADDED
                    onReply={handleReply}
                    onQuote={handleQuote}
                    onReplyClick={scrollToMessage}
                    sxMsgText={{ overflowWrap: 'anywhere' }}
                  />
                )}
              </Box>
            );
          })}

          {/* Status Indicators */}
          {isManualRefresh && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 1,
              position: 'sticky',
              bottom: 0,
              bgcolor: alpha(theme.palette.background.paper, 0.9),
              backdropFilter: 'blur(4px)'
            }}>
              <Chip
                size="small"
                label="Refreshing messages..."
                color="primary"
                variant="outlined"
                icon={<RefreshIcon sx={{ fontSize: 16 }} />}
              />
            </Box>
          )}

          {showPollingIndicator && !isManualRefresh && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 0.5,
              position: 'sticky',
              bottom: 0,
            }}>
              <Chip
                size="small"
                label="Syncing..."
                color="default"
                variant="outlined"
                sx={{
                  fontSize: '0.7rem',
                  height: 24,
                  opacity: 0.7,
                  bgcolor: alpha(theme.palette.background.paper, 0.8),
                  backdropFilter: 'blur(4px)'
                }}
              />
            </Box>
          )}
        </Box>

        {/* Message Input Area */}
        <Box sx={{ paddingBlock: 2, paddingInline: margin }}>
          {children}
          
          {/* Reply Preview */}
          {replyToMessage && (
            <ReplyPreview
              replyToMessage={replyToMessage}
              isQuote={isQuoting}
              onCancel={handleCancelReply}
            />
          )}
          
          {currentTypingUsers.length > 0 && (
            <Box sx={{ px: 1, py: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {getTypingText(currentTypingUsers)}
              </Typography>
            </Box>
          )}

          <Box display="flex" alignItems="center" gap={1}>
            {/* Campaign-Focused Attachment SpeedDial */}
            {isUser && (
              <Box sx={{ width: "56px", height: "56px", position: "relative" }}>
                <SpeedDial
                  ariaLabel="Campaign Attachments"
                  icon={<AttachFileIcon />}
                  sx={{ position: "absolute", bottom: 0, right: 0 }}
                  FabProps={{
                    disabled: sendMessageLoading || messagesLoading || !conversationId
                  }}
                >
                  <SpeedDialAction
                    icon={<MovieCreationIcon />}
                    tooltipTitle="Edit Video Settings"
                    onClick={() => setOpenVideo(true)}
                  />
                  <SpeedDialAction
                    icon={<InsertDriveFileOutlinedIcon />}
                    tooltipTitle="Attach File"
                    onClick={() => setUploadDialogOpen(true)}
                  />
                  <SpeedDialAction
                    icon={<LocalShippingIcon />}
                    tooltipTitle="Shipping Address"
                    onClick={() => setCreatorShippingAddress(true)}
                  />
                </SpeedDial>
              </Box>
            )}

            {/* Creator Attachment Options */}
            {!isUser && (
              <Box sx={{ width: "56px", height: "56px", position: "relative" }}>
                <SpeedDial
                  ariaLabel="Creator Attachments"
                  icon={<AttachFileIcon />}
                  sx={{ position: "absolute", bottom: 0, right: 0 }}
                  FabProps={{
                    disabled: sendMessageLoading || !allMessages.length
                  }}
                >
                  <SpeedDialAction
                    icon={<VideoLibraryIcon />}
                    tooltipTitle="Video Content"
                    onClick={() => setCreatorUploadDialogOpen(true)}
                  />
                  <SpeedDialAction
                    icon={<InsertDriveFileOutlinedIcon />}
                    tooltipTitle="Attach File"
                    onClick={() => setCreatorUploadDialogOpen(true)}
                  />
                  <SpeedDialAction
                    icon={<LocalShippingIcon />}
                    tooltipTitle="Shipping Address"
                    onClick={() => setCreatorShippingAddress(true)}
                  />
                </SpeedDial>
              </Box>
            )}

            {/* Text Field with @mention support */}
            <Box sx={{ flex: 1, position: 'relative' }}>
              <MentionSuggestions
                isVisible={mentions.isVisible}
                participants={mentions.filteredParticipants}
                selectedIndex={mentions.selectedIndex}
                onSelect={(p) => {
                  const newMsg = mentions.insertMention(p, message);
                  setMessage(newMsg);
                }}
                getDisplayName={mentions.getDisplayName}
              />
              <TextField
                multiline
                maxRows={MAX_MESSAGE_ROWS}
                value={message}
                autoComplete='off'
                name='campaign-chat-message'
                inputRef={mentions.inputRef}
                disabled={sendMessageLoading || messagesLoading || !conversationId || !!selectedAttachment}
                onChange={(e) => {
                  setMessage(e.target.value);
                  mentions.onTextChange(e.target.value, e.target.selectionStart);
                }}
                label="Type a message"
                variant="outlined"
                fullWidth
                onBlur={() => setTimeout(() => mentions.closeSuggestions(), 150)}
                onKeyDown={(e) => {
                  if (mentions.isVisible) {
                    if (e.key === 'ArrowDown') { e.preventDefault(); mentions.moveDown(); return; }
                    if (e.key === 'ArrowUp') { e.preventDefault(); mentions.moveUp(); return; }
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      const selected = mentions.filteredParticipants[mentions.selectedIndex];
                      if (selected) { setMessage(mentions.insertMention(selected, message)); }
                      return;
                    }
                    if (e.key === 'Escape') { e.preventDefault(); mentions.closeSuggestions(); return; }
                  }
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                InputProps={{
                  ...(selectedAttachment && {
                    startAdornment: (
                      <InputAdornment position='start'>
                        <Chip
                          icon={getIcon(selectedAttachment.type)}
                          avatar={
                            selectedAttachment.preview ? (
                              <Avatar
                                alt={selectedAttachment.label}
                                src={selectedAttachment.preview}
                              />
                            ) : undefined
                          }
                          label={selectedAttachment.label}
                          sx={{ maxWidth: '14rem' }}
                          onDelete={() => setSelectedAttachment(null)}
                        />
                      </InputAdornment>
                    )
                  })
                }}
              />
            </Box>

            {/* Send Button */}
            <IconButton
              disabled={sendMessageLoading || messagesLoading || !conversationId}
              onClick={handleSendMessage}
              color="primary"
              sx={{
                '&:disabled': {
                  opacity: 0.5
                }
              }}
            >
              {sendMessageLoading ? (
                <CircularProgress size={24} />
              ) : (
                <SendIcon />
              )}
            </IconButton>
          </Box>
        </Box>

        {/* Message Cap Alert */}
        <Snackbar
          open={showMessageCapAlert}
          autoHideDuration={SNACKBAR_DURATION}
          onClose={() => setShowMessageCapAlert(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setShowMessageCapAlert(false)}
            severity="warning"
            sx={{ width: "100%" }}
          >
            Messages since last response: You are capped at{" "}
            {MAX_MESSAGES_WITHOUT_RESPONSE}
          </Alert>
        </Snackbar>

        {/* Reaction Snackbar */}
        <Snackbar
          open={snack.msg !== ''}
          autoHideDuration={3000}
          onClose={() => setSnack({ msg: '', sev: 'info' })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnack({ msg: '', sev: 'info' })}
            severity={snack.sev}
            sx={{ width: "100%" }}
          >
            {snack.msg}
          </Alert>
        </Snackbar>
      </Box>

      {/* NEW: Welcome Dialog that shows automatically */}
      <WelcomeNextStepsDialog
        open={welcomeDialogOpen}
        onClose={handleWelcomeClose}
        isCreator={isCreator}
        campaignStatus={metadata?.campaign?.status || 'Draft'}
        videoProgresses={videoProgresses}
        allMessages={allMessages}
        onVideoDetailsOpen={handleVideoDetailsFromWelcome}
      />

      {/* Next Steps Guidance Dialog */}
      <WelcomeNextStepsDialog
        open={nextStepsDialogOpen}
        onClose={handleGuidanceClose}
        isCreator={isCreator}
        campaignStatus={metadata?.campaign?.status || 'Draft'}
        videoProgresses={videoProgresses}
        allMessages={allMessages}
        onVideoDetailsOpen={handleVideoDetailsFromGuidance}
      />

      {/* Campaign-Specific Dialogs */}
      <EditVideoLinksDialog
        isOpen={openVideoLink}
        campaignId={campaignId}
        creatorId={creatorId}
        videoId={videoLinkId}
        isUser={isUser}
        conversationId={conversationId}
        onClose={() => {
          setOpenVideoLink(false);
          messagesRefetch();
        }}
      />

      <CampaignVideoEditor
        isOpen={openVideo}
        campaignId={campaignId}
        creator={creatorId}
        onClose={() => {
          setOpenVideo(false);
          messagesRefetch();
        }}
      />

      <SelectAssetDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        selectedAsset={selectedAttachment}
        onAssetClick={handleFileSelect}
      />

      <CreatorUploadFileDialog
        open={creatorUploadDialogOpen}
        onClose={() => setCreatorUploadDialogOpen(false)}
        conversationId={conversationId}
        readerId={specificReaderId}
        onFileUpload={handleCreatorFile}
        onError={handleCreatorUploadError}
      />

      <ShippingAddressDialog
        open={creatorShippingAddress}
        onClose={handleShippingSelect}
      />

      <ReadReceipt
        open={currentMessage !== null}
        handleClose={() => setCurrentMessage(null)}
        message={currentMessage}
        participants={conversation?.participants}
        currentUsername={userId}
      />

      <AlertDialog alertState={dialogState} />

      <PdfSigningDialog
        open={pdfSigningDialogOpen}
        onClose={() => setPdfSigningDialogOpen(false)}
        fileData={fileForSigning}
        onFileSigned={(updatedFile) => {
          sendMessage({
            id: conversationId,
            input: {
              message: "",
              attachment: {
                type: "file",
                data: updatedFile
              }
            },
          });
          setPdfSigningDialogOpen(false);
        }}
      />

      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Campaign Workflow</DialogTitle>
        <DialogContent dividers>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="1. Share video requirements and assets" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="2. Creator uploads video content" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="3. Review and approve content" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="4. Track campaign performance" />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)} autoFocus>
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default EnhancedCampaignChatScreen;